import { useEffect } from "react";
import mitt from "mitt";
import { v4 as uuidv4 } from "uuid";
import { broadcastSync, getDeviceId, getDeviceInfo, globalAny, inWatchGroup, isMaster } from "../utils/Utils";
import { AppLogger } from "../utils/AppLogger";
import { FineTuneSync } from "../enums/FineTuneSync";
import useToggleStore from "../store/useToggle.store";
import moment from "moment";
import useMqttStore from "../store/useMqtt.store";
import MqttSenderService from "../services/mqtt/MqttSenderService";
import MqttReceiverService from "../services/mqtt/MqttReceiverService";
import { MqttCommand } from "../models/mqtt-command";
import { AsyncStorageKeys } from "../Types";
import { IGNORE_COMMAND_MS } from "../constants";
import { PlayerState } from "../enums/PlayerState";
const PlayerEventHandlerExternal = mitt();

const useMqttService = () => {
	const isToggleMQTT = useToggleStore((state: any) => state.isToggleMQTT);
	const mqttSender: MqttSenderService = useMqttStore((state: any) => state.mqttSender);
	const mqttReceiver: MqttReceiverService = useMqttStore((state: any) => state.mqttReceiver);
	const setIsStop = useMqttStore((state: any) => state.setIsStop);
	const isMqttMessageAckEnabled = useToggleStore((state: any) => state.isMqttMessageAckEnabled);
	let defaultPlaySessionId = uuidv4();

	/**
	 * Function to handle large jumps in video playback by setting the playback rate to normal and updating the current time
	 * @param videoElement
	 * @param targetPosition
	 */
	const syncLargeJump = (videoElement: any, targetPosition: number) => {
		AppLogger.log("[SYNC]: Large Jump");
		videoElement.playbackRate = 1;
		videoElement.currentTime = targetPosition;
	};

	/**
	 * Function to fine-tune the playback rate of the video element based on the position difference
	 * @param videoElement
	 * @param posDiff
	 */
	const syncFineTune = (videoElement: any, posDiff: number) => {
		if (globalAny.fineTuneEnabled || posDiff > FineTuneSync.EnableRange || posDiff < -FineTuneSync.EnableRange) {
			globalAny.fineTuneEnabled = true;
			videoElement.mozPreservesPitch = true;
			videoElement.preservesPitch = true;

			if (posDiff <= -FineTuneSync.LargeRange) {
				videoElement.playbackRate = 0.6;
			} else if (posDiff > -FineTuneSync.LargeRange && posDiff <= -FineTuneSync.MediumRange) {
				videoElement.playbackRate = 0.8;
			} else if (posDiff > -FineTuneSync.MediumRange && posDiff <= -FineTuneSync.SmallRange) {
				videoElement.playbackRate = 0.9;
			} else if (posDiff >= FineTuneSync.LargeRange) {
				videoElement.playbackRate = 1.4;
			} else if (posDiff >= FineTuneSync.MediumRange && posDiff < FineTuneSync.LargeRange) {
				videoElement.playbackRate = 1.2;
			} else if (posDiff >= FineTuneSync.SmallRange && posDiff < FineTuneSync.MediumRange) {
				videoElement.playbackRate = 1.1;
			} else {
				globalAny.fineTuneEnabled = false;
				videoElement.playbackRate = 1;
			}

			AppLogger.log("[SYNC]: Fine Tuning", {
				posDiff,
				playBackRate: videoElement.playbackRate,
				leaderPos: globalAny.lastKnownLeaderPosition,
			});
		}
	};

	/**
	 *  Function to synchronize the media position of non-master devices in a watch group
	 * @returns
	 */
	const syncMediaPosition = () => {
		const position = globalAny.lastKnownLeaderPosition;

		if (!isToggleMQTT) return;

		if (
			isMaster(globalAny.mqttPlayContent, getDeviceInfo()) ||
			!inWatchGroup(globalAny.mqttPlayContent) ||
			!globalAny.videoElement ||
			position > globalAny.videoElement?.duration ||
			globalAny.ignoreCommands
		)
			return;

		AppLogger.log("[SYNC]: Sync Media Position");

		let timeSinceLastKnownLeaderPosition = (moment().valueOf() - globalAny.timeLeaderPosition) / 1000;
		globalAny.timeSinceLastKnownLeaderPosition = timeSinceLastKnownLeaderPosition;
		if (globalAny.videoElement.paused) timeSinceLastKnownLeaderPosition = 0;

		let targetPosition = position + timeSinceLastKnownLeaderPosition + globalAny.lastKnownLatency;

		globalAny.targetPosition = targetPosition;

		let posDiff = targetPosition - globalAny.videoElement?.currentTime;

		globalAny.posDiff = posDiff;

		// jump to specific timeframe if difference is too big
		if (posDiff >= 2 || posDiff <= -2) {
			syncLargeJump(globalAny.videoElement, targetPosition);
			return;
		}

		syncFineTune(globalAny.videoElement, posDiff);
	};

	/**
	 * Function to start the synchronization interval for non-master devices in a watch group
	 * @returns
	 */
	const startSyncInterval = () => {
		if (!inWatchGroup(globalAny.mqttPlayContent) && globalAny.syncMediaPositionTimer) {
			clearInterval(globalAny.syncMediaPositionTimer);
			globalAny.syncMediaPositionTimer = null;
		}

		if (inWatchGroup(globalAny.mqttPlayContent) && globalAny.videoElement) {
			if (globalAny.syncMediaPositionTimer) return;

			globalAny.syncMediaPositionTimer = setInterval(syncMediaPosition, 20);
		}
	};

	/**
	 * Function for updating the master player position
	 * @param decodedBody
	 * @returns
	 */
	const updateMasterPosition = (decodedBody: any) => {
		if (!globalAny.videoElement) return;
		globalAny.videoElement.currentTime = decodedBody.position / 1000;
		globalAny.videoElement.play();
	};

	/**
	 * Function to handle controller commands from the master device
	 * @param decodedBody
	 * @param message
	 * @returns
	 */
	const controllerCommand = (decodedBody: any, message: any) => {
		const currentDevice = getDeviceInfo();

		if (!globalAny.videoElement) return;

		if (
			decodedBody.state !== PlayerState.Stopped &&
			inWatchGroup(globalAny.mqttPlayContent) &&
			isMaster(globalAny.mqttPlayContent, currentDevice)
		) {
			globalAny.ignoreCommands = true;
			globalAny.videoElement.currentTime = decodedBody.position / 1000;
			globalAny.videoElement?.play();
			setTimeout(() => (globalAny.ignoreCommands = false), IGNORE_COMMAND_MS);
		}

		if (decodedBody.state === PlayerState.Stopped) {
			globalAny.ignoreCommands = true;
			setTimeout(() => (globalAny.ignoreCommands = false), IGNORE_COMMAND_MS);
			return;
		}

		if (decodedBody.state === PlayerState.Paused) {
			globalAny.ignoreCommands = true;
			globalAny.videoElement?.pause();
			setTimeout(() => (globalAny.ignoreCommands = false), IGNORE_COMMAND_MS);
			return;
		}

		if (decodedBody.state !== PlayerState.Paused && decodedBody.position === 0) {
			globalAny.ignoreCommands = true;
			globalAny.videoElement.currentTime = 0;
			setTimeout(() => {
				globalAny.ignoreCommands = false;
				mqttSender?.broadcastPositionCommand(globalAny.mqttPlayContent);
			}, IGNORE_COMMAND_MS);
		}

		globalAny.videoElement?.play();
	};

	/**
	 * Function for resume receiver event listener
	 * @param message
	 */
	const onMediaPlayListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-play: ", message);
		const decodedBody = message?.body;
		const deviceId = getDeviceId();

		if (deviceId === decodedBody?.deviceId) {
			globalAny.videoElement?.play();
			mqttSender?.broadcastPositionCommand({ ...globalAny.mqttPlayContent, state: PlayerState.Playing });
		}
	};

	/**
	 * Function for pause receiver event listener
	 * @param message
	 */
	const onMediaPauseListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-pause: ", message);
		const decodedBody = message.body;
		const deviceId = getDeviceId();

		if (deviceId === decodedBody?.deviceId) {
			globalAny.videoElement?.pause();
			mqttSender?.broadcastPositionCommand({ ...globalAny.mqttPlayContent, state: PlayerState.Paused });
		}
	};

	/**
	 * Function for restart receiver event listener
	 * @param message
	 */
	const onMediaRestartListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-restart: ", message);
		const decodedBody = message?.body;
		const deviceId = getDeviceId();

		if (deviceId === decodedBody?.deviceId) {
			globalAny.videoElement.currentTime = 0;
		}
	};

	/**
	 * Function for stop receiver event listener
	 * @param message
	 */
	const onMediaStopListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-stop: ", message);
		const decodedBody = message?.body;
		const deviceId = getDeviceId();

		if (deviceId === decodedBody?.deviceId) {
			setIsStop(true);
		}
	};

	/**
	 * Function for seek receiver event listener
	 * @param message
	 */
	const onMediaSeekListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-seek: ", message);
		const playSessionId: any = localStorage.getItem(AsyncStorageKeys.playSessionId);
		const decodedBody = message?.body;
		const isProceed = decodedBody.playSessionId === playSessionId;

		if (!inWatchGroup(globalAny.mqttPlayContent) || !isMaster(globalAny.mqttPlayContent, getDeviceInfo()) || !isProceed || !playSessionId) return;

		updateMasterPosition(decodedBody);
	};

	/**
	 * Function to receive command from Master device
	 * @param message
	 */
	const onMediaSyncListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-sync: ", message);
		const deviceId = getDeviceId();
		const decodedBody = message?.body;
		const playSessionId: any = localStorage.getItem(AsyncStorageKeys.playSessionId) ?? defaultPlaySessionId;
		const isSenderSameDeviceId = deviceId === message.deviceId;
		const isReceiverSameDeviceId = deviceId === decodedBody.deviceId;
		const isSamePlaySessionId = playSessionId === decodedBody.playSessionId;

		if (!playSessionId || !isSamePlaySessionId || isSenderSameDeviceId) return;

		if (mqttSender && isMqttMessageAckEnabled && isReceiverSameDeviceId && deviceId && !isMaster(globalAny.mqttPlayContent, getDeviceInfo()))
			mqttSender.messageAcknowledgement({
				ackMessageId: message.messageId,
				deviceId,
				screenId: deviceId,
			});

		globalAny.lastKnownLeaderPosition = decodedBody.position / 1000;
		globalAny.timeLeaderPosition = moment().valueOf();

		if (isSamePlaySessionId && isReceiverSameDeviceId) {
			startSyncInterval();
			controllerCommand(decodedBody, message);
		}
	};

	/**
	 * Function to handle message acknowledgement events.
	 * @param message
	 */
	const onMessageAcknowledgementListener = (message: any) => {
		AppLogger.log("[MQTT] Received message-ack: ", message);
		const { ackMessageId } = message.body;
		if (
			inWatchGroup(globalAny.mqttPlayContent) &&
			isMaster(globalAny.mqttPlayContent, getDeviceInfo()) &&
			globalAny.messageIds.hasOwnProperty(ackMessageId) &&
			mqttSender
		) {
			const ackReceived: number = moment().valueOf();
			const latencySec = (ackReceived - globalAny.messageIds[ackMessageId]) / 1000;
			const oneWayLatency = latencySec / 2;
			globalAny.lastKnownLatency = oneWayLatency;
			broadcastSync(
				{
					...globalAny.mqttPlayContent,
					confirmMessageId: ackMessageId,
					confirmedLatency: oneWayLatency,
				},
				mqttSender,
				true,
			);

			delete globalAny.messageIds[ackMessageId];
		}
	};

	/**
	 * Function to handle media sync confirm messages
	 * @param message
	 * @returns
	 */
	const onMediaSyncConfirmListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-sync-confirm: ", message);
		const deviceInfo = getDeviceInfo();
		const { playSessionId, _confirmMessageId, confirmedLatency, deviceId } = message.body;
		const isReceiverSameDeviceId = deviceInfo.deviceId === deviceId;

		if (playSessionId !== globalAny.mqttPlayContent?.playSessionId) return;

		if (inWatchGroup(globalAny.mqttPlayContent) && !isMaster(globalAny.mqttPlayContent, getDeviceInfo()) && isReceiverSameDeviceId) {
			globalAny.lastKnownLatency = confirmedLatency;
		}
	};

	/**
	 * Function to handle ping messages
	 * @param message
	 */
	const onPingListener = (message: any) => {
		const decodedBody = message.body;
		AppLogger.log("[MQTT-PING] Ping Received:", message);

		if (
			inWatchGroup(globalAny.mqttPlayContent) &&
			isMaster(globalAny.mqttPlayContent, getDeviceInfo()) &&
			decodedBody.deviceId === getDeviceId()
		) {
			mqttSender.sendPingBackCommand(decodedBody.pingId, message.deviceId);
		}
	};

	/**
	 * Function to handle ping back messages
	 * @param message
	 */
	const onPingBackListener = (message: any) => {
		const decodedBody = message.body;
		AppLogger.log("[MQTT-PING] Ping Back Received:", decodedBody);

		if (globalAny.pingSent.hasOwnProperty(decodedBody.pingId) && decodedBody.deviceId === getDeviceId()) {
			const pingBackReceived: number = moment().valueOf();
			const latencySec = (pingBackReceived - globalAny.pingSent[decodedBody.pingId]) / 1000;
			globalAny.lastKnownLatency = latencySec / 2;
			delete globalAny.pingSent[decodedBody.pingId];
		}
	};

	/**
	 * Function to handle subtitle change messages
	 * @param message
	 * @returns
	 */
	const onMediaSubtitleListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-subtitle: ", message);
		const playSessionId: any = localStorage.getItem(AsyncStorageKeys.playSessionId);
		const decodedBody = message.body;
		if (!playSessionId) return;

		let groupDevices: any = globalAny.watchGroup;
		groupDevices = JSON.parse(groupDevices);
		let currentPlaySessionId = "";

		groupDevices?.forEach((groups: any) => {
			if (groups.devices.length === 0) return;
			groups.devices?.forEach((device: any) => {
				if (device.deviceId === message.deviceId) currentPlaySessionId = groups.playSessionId;
			});
		});

		if (playSessionId === currentPlaySessionId) {
			PlayerEventHandlerExternal.emit("onSubTitleChanged", decodedBody.subtitle);
		}
	};

	/**
	 * Function to handle audio change messages
	 * @param message
	 * @returns
	 */
	const onMediaAudioListener = (message: any) => {
		AppLogger.log("[MQTT] Received media-audio: ", message);
		const playSessionId: any = localStorage.getItem(AsyncStorageKeys.playSessionId);
		const decodedBody = message.body;
		if (!playSessionId) return;

		let groupDevices: any = globalAny.watchGroup;
		groupDevices = JSON.parse(groupDevices);
		let currentPlaySessionId = "";

		groupDevices?.forEach((groups: any) => {
			if (groups.devices.length === 0) return;
			groups.devices?.forEach((device: any) => {
				if (device.deviceId === message.deviceId) currentPlaySessionId = groups.playSessionId;
			});
		});

		if (playSessionId === currentPlaySessionId) {
			PlayerEventHandlerExternal.emit("onAudioChanged", decodedBody.audio);
		}
	};

	/**
	 * Effect to start the synchronization interval when mqttPlayContent or videoElement changes
	 */
	useEffect(() => {
		startSyncInterval();
	}, [globalAny.mqttPlayContent, globalAny.videoElement]);

	/**
	 * Effect to handle MQTT receiver event listeners
	 * Sets up event listeners for various MQTT commands when the mqttReceiver is available and MQTT toggle is enabled
	 * Cleans up the event listeners when the component unmounts or when dependencies change
	 */
	useEffect(() => {
		if (!mqttReceiver || !isToggleMQTT) return;
		let pingBackListener: any, pingListener: any;
		let messageAck: any, mediaSyncConfirm: any;
		const mediaPlayListener = mqttReceiver.addOnMediaPlayListener(onMediaPlayListener);
		const mediaPauseListener = mqttReceiver.addOnMediaPauseListener(onMediaPauseListener);
		const mediaRestartListener = mqttReceiver.addOnMediaRestartListener(onMediaRestartListener);
		const mediaStopListener = mqttReceiver.addOnMediaStopListener(onMediaStopListener);
		const mediaSeekListener = mqttReceiver.addOnMediaSeekListener(onMediaSeekListener);
		const mediaSyncListener = mqttReceiver.addOnMediaSyncListener(onMediaSyncListener);
		const mediaSubtitleListener = mqttReceiver.addOnSubTitleChange(onMediaSubtitleListener);
		const mediaAudioListener = mqttReceiver.addOnAudioChange(onMediaAudioListener);

		if (isMqttMessageAckEnabled) {
			messageAck = mqttReceiver.addOnMessageAcknowledgementEvents(onMessageAcknowledgementListener);
			mediaSyncConfirm = mqttReceiver.addOnMediaSyncConfirmEvents(onMediaSyncConfirmListener);
		} else {
			pingListener = mqttReceiver.addOnPingReceivedListener(onPingListener);
			pingBackListener = mqttReceiver.addOnPingBackReceivedListener(onPingBackListener);
		}

		return () => {
			mqttReceiver.deleteEventListener(MqttCommand.MediaPlay, mediaPlayListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaPause, mediaPauseListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaRestart, mediaRestartListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaStop, mediaStopListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaSeek, mediaSeekListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaSync, mediaSyncListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaSubtitle, mediaSubtitleListener);
			mqttReceiver.deleteEventListener(MqttCommand.MediaAudio, mediaAudioListener);

			if (isMqttMessageAckEnabled) {
				mqttReceiver.deleteEventListener(MqttCommand.MessageAcknowledgement, messageAck);
				mqttReceiver.deleteEventListener(MqttCommand.MediaSyncConfirm, mediaSyncConfirm);
			} else {
				mqttReceiver.deleteEventListener(MqttCommand.Ping, pingListener);
				mqttReceiver.deleteEventListener(MqttCommand.PingBack, pingBackListener);
			}
		};
	}, []);
};

export default useMqttService;
